import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SparkleIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import { colors } from '../../../themes/typography';
import { getExperimentStatusColor } from '../../../themes/experimentColors';
import { startsTooSoonForErp } from '../../../constants/experiments';
import { getApiErrorMessage } from '../../../utils/apiError';
import { shortName } from '../../../utils/productShortName';
import { inlineUpdateExperiment } from '../../../data/problemsApi';
import { fetchCandidateSuggestion } from '../../ExperimentManagement/services/experimentManagementApi';
import { useRejectionModal } from '../../../hooks/useRejectionModal';
import { alternativeOptions, useReplacementAlternatives } from '../../../components/shared/ReplacementAlternatives';
import { IncomingProductAutocomplete, ExperimentStartFields } from '../../../components/shared/IncomingProductPicker';
import AppliedPricingDialog from '../../../components/shared/AppliedPricingDialog';
import RejectReasonDialog from '../../../components/RejectReasonDialog/RejectReasonDialog';
import LaneCandidateList, { useLaneCandidates } from './LaneCandidateList';

const SUGGESTION = getExperimentStatusColor('manual_review');
// The proposals whose product coming in can be changed before accepting
const REPLACEMENT_TYPES = ['reemplazo_producto', 'ampliar_carril'];
const WEEK_MS = 7 * 86400000;

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// The day an ISO date names, at local midnight, whatever the browser's time zone
const localDay = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

// The orchestrator's *bold*
const Emphasis = ({ text }) => text.split(/\*([^*]+)\*/g).map((part, i) => (i % 2 ? <b key={i}>{part}</b> : part));

/**
 * The orchestrator's suggestion on the lanes clicked in experiment mode, in the panel at the planogram's right: what
 * comes in — its alternatives (más margen / más volumen: the saved ones, else the orchestrator asked now), the best
 * products for its lanes, or any product searched — and when, then Aceptar or Rechazar, the reason picked like in
 * Problemas. The product and dates go in the accept request itself, as in
 * Problemas: the nightly run rewrites a pending proposal, so nothing is saved before.
 */
const ExperimentSuggestion = ({ clientId, machineId, experiment, lanes, allProducts, locale, onClose, onResolved }) => {
  const { t } = useTranslation();
  const isReplacement = REPLACEMENT_TYPES.includes(experiment.experimentType);
  const originalId = Number(experiment.originalProduct?.id);
  const originName = shortName(experiment.originalProduct?.name || lanes[0]?.name);
  const proposedId = Number(experiment.candidateProductIds?.[0]) || null;
  const [incoming, setIncoming] = useState(
    proposedId ? { id: proposedId, name: experiment.candidateNames?.[0] || `#${proposedId}` } : null
  );
  const fromDay = useMemo(() => localDay(experiment.dateFrom), [experiment.dateFrom]);
  const toDay = useMemo(() => localDay(experiment.dateTo), [experiment.dateTo]);
  const [startDate, setStartDate] = useState(fromDay);
  const [weeks, setWeeks] = useState(() => Math.min(52, Math.max(1, Math.round((toDay - fromDay) / WEEK_MS))));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [priceReview, setPriceReview] = useState(null);

  const endDate = useMemo(() => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + weeks * 7);
    return d;
  }, [startDate, weeks]);

  // A proposal saved without alternatives gets the orchestrator's, asked now, as the drawer's sparkle does
  const saved = useReplacementAlternatives(clientId, experiment.id, isReplacement);
  const savedEmpty = Boolean(saved) && !saved.margin?.length && !saved.volume?.length;
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!savedEmpty) return undefined;
    let cancelled = false;
    fetchCandidateSuggestion(clientId, machineId, originalId, 'reemplazo_producto').then((s) => {
      if (!cancelled) setLive({ margin: s?.candidates || [], volume: s?.volumeCandidates || [] });
    });
    return () => { cancelled = true; };
  }, [savedEmpty, clientId, machineId, originalId]);
  const alternatives = useMemo(
    () => alternativeOptions((savedEmpty ? live : saved) || {}).map((o) => ({ ...o, id: o.productId, name: o.productName })),
    [saved, live, savedEmpty]
  );
  const { candidates, originLaneCapacities } = useLaneCandidates(clientId, machineId, `${originalId}:${lanes.length}`, isReplacement);

  // Only what was changed goes with the acceptance
  const datesChanged = fmtDate(startDate) !== fmtDate(fromDay) || fmtDate(endDate) !== fmtDate(toDay);
  const updates = {
    status: 'accepted',
    ...(datesChanged && { dateFrom: fmtDate(startDate), dateTo: fmtDate(endDate) }),
    ...(isReplacement && incoming && Number(incoming.id) !== proposedId && { candidateProductId: Number(incoming.id) }),
  };

  // The prices the change will apply are confirmed first, as everywhere an experiment is accepted
  const accept = async (pricingOverride = null) => {
    setBusy(true);
    setMessage(null);
    try {
      if (!pricingOverride) {
        const { data } = await axios.post(
          `${API_BASE_URL}/experiments/pricing-preview?clientId=${encodeURIComponent(clientId)}`,
          { experimentId: experiment.id, ...updates },
          { headers: createAuthHeader(localStorage.getItem('access_token')) }
        );
        if (data?.pricing) {
          setPriceReview(data.pricing);
          setBusy(false);
          return;
        }
      }
      await inlineUpdateExperiment(clientId, experiment.id, { ...updates, ...(pricingOverride && { pricingOverride }) });
      onResolved();
    } catch (error) {
      setMessage(getApiErrorMessage(error, t('actionFailed')));
      setBusy(false);
    }
  };

  const rejectModal = useRejectionModal(async (experimentId, reason) => {
    setBusy(true);
    setMessage(null);
    try {
      await inlineUpdateExperiment(clientId, experimentId, { status: 'rejected', feedback: reason });
      onResolved();
    } catch (error) {
      setMessage(getApiErrorMessage(error, t('actionFailed')));
      setBusy(false);
    }
  });

  const why = experiment.aiRecommendation?.[0];

  return (
    <>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 0.75, pr: 0.25, py: '2px', maxWidth: '100%',
        borderRadius: '12px', backgroundColor: `${SUGGESTION}12`, border: `1px solid ${SUGGESTION}40`,
      }}>
        <SparkleIcon sx={{ fontSize: 12, color: SUGGESTION }} />
        <Typography noWrap sx={{ fontSize: '11px', color: colors.text.primary, minWidth: 0 }}>
          {t('machines_aiSuggestion')} <Box component="span" sx={{ color: colors.text.secondary }}>· {lanes.map((s) => s.selection_id).join(', ')}</Box>
        </Typography>
        <IconButton size="small" aria-label={t('exp_back')} disabled={busy} onClick={onClose} sx={{ p: '1px' }}>
          <CloseIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Box>

      {why && (
        <Typography sx={{ fontSize: '11px', color: colors.text.secondary, lineHeight: 1.4, mt: 1 }}>
          <Emphasis text={why} />
        </Typography>
      )}

      {/* What goes out, and what comes in instead: the proposal, or another product for its lanes */}
      {isReplacement ? (
        <>
          <Typography sx={{ fontSize: '11px', color: colors.text.secondary, mt: 1.25, mb: 0.5 }}>{originName} →</Typography>
          <IncomingProductAutocomplete
            value={incoming}
            alternatives={alternatives}
            allProducts={allProducts}
            excludeId={originalId}
            disabled={busy}
            placeholder={t('machines_searchProduct')}
            onChange={setIncoming}
          />
          <Box sx={{ mt: 1 }}>
            <LaneCandidateList
              candidates={candidates}
              originLaneCapacities={originLaneCapacities}
              originName={originName}
              chosenId={incoming?.id}
              disabled={busy}
              onPick={(c) => setIncoming({ id: c.productId, name: c.name })}
            />
          </Box>
        </>
      ) : (
        <Typography sx={{ fontSize: '11.5px', color: colors.text.primary, mt: 1.25 }}>{experiment.description}</Typography>
      )}

      <ExperimentStartFields
        startDate={startDate}
        onStartDateChange={setStartDate}
        weeks={weeks}
        onWeeksChange={setWeeks}
        endDate={endDate}
        locale={locale}
      />
      {message && <Typography sx={{ fontSize: '11px', color: '#b45309', mt: 0.75 }}>{message}</Typography>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
        <Button size="small" onClick={onClose} disabled={busy} sx={{ textTransform: 'none', fontSize: '12px', color: colors.text.secondary, mr: 'auto' }}>
          {t('exp_back')}
        </Button>
        <Button
          size="small" variant="outlined" disabled={busy} onClick={() => rejectModal.openRejectDialog(experiment.id)}
          sx={{ textTransform: 'none', fontSize: '12px', borderRadius: '8px', color: '#d32f2f', borderColor: '#ef535080', '&:hover': { borderColor: '#d32f2f', backgroundColor: '#ef53500a' } }}
        >
          {t('reject')}
        </Button>
        <Button
          size="small" variant="contained" disableElevation onClick={() => accept()}
          disabled={busy || (isReplacement && !incoming) || startsTooSoonForErp(startDate)}
          startIcon={busy ? <CircularProgress size={12} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontSize: '12px', borderRadius: '8px', backgroundColor: '#2f2a3d', '&:hover': { backgroundColor: '#1f1b2b' } }}
        >
          {t('accept')}
        </Button>
      </Box>

      <AppliedPricingDialog
        open={Boolean(priceReview)}
        pricing={priceReview}
        onClose={() => setPriceReview(null)}
        onConfirm={(pricingOverride) => { setPriceReview(null); accept(pricingOverride); }}
      />
      <RejectReasonDialog {...rejectModal.dialogProps} loading={busy} />
    </>
  );
};

export default ExperimentSuggestion;
